import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import s from "../clinic.module.css";

const PAD = 10;

function measureTarget(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PAD,
    left: r.left - PAD,
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
    bottom: r.bottom + PAD,
    right: r.right + PAD,
    centerX: r.left + r.width / 2,
    centerY: r.top + r.height / 2,
  };
}

/**
 * Подсветка шага онбординга. Не блокирует скролл страницы.
 */
export default function OnboardingSpotlight({
  targetSelector,
  title,
  description,
  stepLabel,
  onTargetActivate,
  onSkip,
  placement = "bottom",
}) {
  const [hole, setHole] = useState(null);

  const remeasure = useCallback(() => {
    setHole(measureTarget(targetSelector));
  }, [targetSelector]);

  useEffect(() => {
    remeasure();
    const el = document.querySelector(targetSelector);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        remeasure();
      }, 80);
    }
    const t = window.setInterval(remeasure, 250);
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [remeasure, targetSelector]);

  useEffect(() => {
    const el = document.querySelector(targetSelector);
    if (!el) return undefined;
    const prevZ = el.style.zIndex;
    const prevPos = el.style.position;
    const computed = window.getComputedStyle(el);
    if (computed.position === "static") el.style.position = "relative";
    el.style.zIndex = "10001";
    el.setAttribute("data-onboarding-highlight", "1");

    const onClick = (e) => {
      e.stopPropagation();
      onTargetActivate?.();
    };
    el.addEventListener("click", onClick, true);

    return () => {
      el.removeEventListener("click", onClick, true);
      el.style.zIndex = prevZ;
      el.style.position = prevPos;
      el.removeAttribute("data-onboarding-highlight");
    };
  }, [targetSelector, onTargetActivate]);

  if (!hole) return null;

  const tooltipTop =
    placement === "top" ? hole.top - 12 : hole.bottom + 16;
  const tooltipLeft = Math.min(
    Math.max(hole.centerX, 180),
    window.innerWidth - 180,
  );

  const arrowStyle =
    placement === "top"
      ? {
          bottom: -8,
          left: "50%",
          transform: "translateX(-50%) rotate(180deg)",
        }
      : {
          top: -8,
          left: "50%",
          transform: "translateX(-50%)",
        };

  return createPortal(
    <div className={s.onboardingSpotlightRoot} role="presentation">
      <div
        className={s.onboardingSpotlightDim}
        style={{
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
        }}
        aria-hidden
      />
      <div
        className={s.onboardingSpotlightTooltip}
        style={{
          top: placement === "top" ? tooltipTop - 120 : tooltipTop,
          left: tooltipLeft,
          transform: "translateX(-50%)",
        }}
        role="dialog"
        aria-labelledby="onboarding-spotlight-title"
      >
        <span className={s.onboardingSpotlightArrow} style={arrowStyle} aria-hidden />
        {stepLabel ? (
          <span className={s.onboardingSpotlightStep}>{stepLabel}</span>
        ) : null}
        <h4 id="onboarding-spotlight-title" className={s.onboardingSpotlightTitle}>
          {title}
        </h4>
        <p className={s.onboardingSpotlightDesc}>{description}</p>
        <p className={s.onboardingSpotlightHint}>
          Нажмите на подсвеченную карточку или кнопку ниже
        </p>
        <div className={s.onboardingSpotlightActions}>
          <button
            type="button"
            className={s.onboardingSpotlightPrimary}
            onClick={() => onTargetActivate?.()}
          >
            Открыть шаг
          </button>
          <button
            type="button"
            className={s.onboardingSpotlightSkip}
            onClick={onSkip}
          >
            Пропустить подсказки
          </button>
        </div>
      </div>
      <div className={s.onboardingSpotlightFooter}>
        <button type="button" className={s.onboardingSpotlightFooterSkip} onClick={onSkip}>
          Закрыть обучение
        </button>
      </div>
    </div>,
    document.body,
  );
}
